import React from "react";
import { Dialog, Button, Message } from "../Base";
// import { globalApi } from "../../api/global";
import { AppDownloadDialogProps } from "./AppDownloadDialog.types";
import "./AppDownloadDialog.scss";
import utils from "../../utils/utils";
import { showSafariAddHomeScreen } from "../../stores/GlobalState";
import appdownloadLogoImg from "@/assets/appDownload/appdownloadLogo.webp";
import { useT } from "../../i18n/hooks";
import { useCallback, useEffect, useState } from "react";
import { useMemo } from "react";
import { PwaInstallingMask } from "../PwaInstallingMask";
import { usePlatformLogo } from "../../modules/homeDisplay/hooks";
import { useDownloadContext } from "../../hooks/useDownload";

export const AppDownloadDialog = ({
  visible,
  onClose,
}: AppDownloadDialogProps) => {
  const t = useT();
  const platformLogo = usePlatformLogo();
  const isIos = useMemo(() => utils.judgeClient() === "IOS", []);
  const isPwa = useMemo(() => utils.updateCheckIsPwa(), []);
  const isAppNative = useMemo(() => utils.isApp(), []);
  const { handlePwaButtonClick } = useDownloadContext();

  const [pwaInstalled, setPwaInstalled] = useState(() =>
    utils.checkPwaEverInstalled()
  );
  const [pwaLoading, setPwaLoading] = useState(false);

  useEffect(() => {
    const onInstalled = () => setPwaInstalled(true);
    window.addEventListener("PWA_APP_INSTALLED", onInstalled);
    return () => window.removeEventListener("PWA_APP_INSTALLED", onInstalled);
  }, []);

  const handleAndroidDownload = () => {
    const envInfo = utils.getEnvInfo();
    const isKnownInApp =
      envInfo.os !== "iOS" && envInfo.browser.endsWith("InApp");

    if (platformLogo?.appDownloadMethod === "GP" && platformLogo.gpLink) {
      if (isKnownInApp && !isPwa) {
        utils.openDownloadInBrowser(platformLogo.gpLink);
      } else {
        window.open(platformLogo.gpLink, "_blank");
        onClose();
      }
      return;
    }

    if (platformLogo?.appDownloadMethod === "APK" && platformLogo.apkFile) {
      const url = utils.getApkDownloadUrl(platformLogo.apkFile);
      if (isKnownInApp) {
        utils.openDownloadInBrowser(url);
        return;
      }
      utils.downloadLink(url);
      onClose();
      return;
    }

    Message.failed(t("app.androidDownloadFailed"));
  };

  const handleIosDownload = () => {
    try {
      if (platformLogo?.enableIosDownload && platformLogo.iosDownloadLink) {
        window.open(platformLogo.iosDownloadLink, "_blank");
      } else {
        const envInfo = utils.getEnvInfo();
        if (envInfo.context === "InAppWebView" && envInfo.os === "iOS") {
          utils.openExternalBrowserIOS(window.location.href);
        } else {
          showSafariAddHomeScreen(true);
        }
      }
    } catch (error) {
      console.error("iOS get download link failed:", error);
      Message.failed(t("app.iosDownloadFailed"));
    }
  };

  const handlePwaClick = useCallback(async () => {
    if (pwaInstalled) {
      utils.openPwaApp();
      return;
    }
    setPwaLoading(true);
    try {
      await handlePwaButtonClick(undefined, handleAndroidDownload);
    } finally {
      setPwaLoading(false);
    }
  }, [pwaInstalled, handlePwaButtonClick, handleAndroidDownload]);

  return (
    <>
      <PwaInstallingMask />
      <Dialog
        className="app-download-dialog"
        visible={visible}
        onClose={onClose}
        showCloseButton={true}
        closeButtonPosition="bottom-center"
        content={
          <div className="app-download-dialog__content">
            <div className="app-download-dialog__title-section">
              <div
                className="app-download-dialog__title-text"
                style={{ whiteSpace: "pre-line" }}
              >
                {t("app.appDownloadTitle")}
              </div>
              <div className="app-download-dialog__bg-right">
                <img src={appdownloadLogoImg} alt="" />
              </div>
            </div>

            <div className="app-download-dialog__body">
              {isIos ? (
                <div className="app-download-dialog__download-row app-download-dialog__download-row--ios">
                  <img
                    className="app-download-dialog__download-icon"
                    src={utils.getImageUrl(window._config?.icon || "")}
                    alt="iOS"
                  />
                  <span className="app-download-dialog__download-text">
                    {t("app.installIos")}
                  </span>
                  <Button
                    onClick={handleIosDownload}
                    variant="secondary"
                    size="sm"
                    className="app-download-dialog__download-button app-download-dialog__download-button--ios"
                  >
                    {t("app.download")}
                  </Button>
                </div>
              ) : (
                <>
                  {/* PWA 行：APK 原生 App 打开时显示 */}
                  {!isPwa && (
                    <div className="app-download-dialog__download-row app-download-dialog__download-row--android">
                      <img
                        className="app-download-dialog__download-icon"
                        src={utils.getImageUrl(window._config?.icon || "")}
                        alt="PWA"
                      />
                      <span className="app-download-dialog__download-text">
                        {t("app.installAndroidPWA")}
                      </span>
                      <Button
                        onClick={handlePwaClick}
                        loading={pwaLoading}
                        disabled={pwaLoading}
                        variant="primary"
                        className="app-download-dialog__download-button"
                      >
                        {pwaInstalled ? t("app.open") : t("common.download")}
                      </Button>
                    </div>
                  )}

                  {/* App 行：PWA 打开时显示 */}
                  {!isAppNative && (
                    <div className="app-download-dialog__download-row app-download-dialog__download-row--android">
                      <img
                        className="app-download-dialog__download-icon"
                        src={utils.getImageUrl(window._config?.icon || "")}
                        alt="App"
                      />
                      <span className="app-download-dialog__download-text">
                        {t("app.installAndroidApp")}
                      </span>
                      <Button
                        onClick={
                          utils.checkIsIos()
                            ? handleIosDownload
                            : handleAndroidDownload
                        }
                        variant="secondary"
                        className="app-download-dialog__download-button"
                      >
                        {t("common.download")}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        }
      />
    </>
  );
};

export default AppDownloadDialog;
